import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { ArrowRight, Star, TrendingUp, Users, ShoppingBag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "../components/ui/carousel";
import { toast } from 'sonner';

export default function Home() {
  const { t } = useLanguage();
  const [popularProducts, setPopularProducts] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [stats, setStats] = useState({ products: 0, sellers: 0, orders: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [popRes, newRes, sellersRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/products?sort=popular&limit=6`),
          axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/products?sort=latest&limit=8`),
          axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/sellers`)
        ]);
        setPopularProducts(popRes.data);
        setNewArrivals(newRes.data);
        setSellers(sellersRes.data);
      } catch (error) {
        console.error("Failed to load home data", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="overflow-hidden bg-stone-50">
      
      {/* Hero Section */}
      <section className="relative h-[85vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1629498899964-070d3174bfd3?crop=entropy&cs=srgb&fm=jpg&q=85" 
            alt="Algerian Market Crafts" 
            className="w-full h-full object-cover animate-in fade-in zoom-in duration-1000"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-stone-50"></div>
        </div>

        <div className="relative z-10 text-center px-4 max-w-5xl mx-auto space-y-8">
          <div className="inline-block animate-in slide-in-from-bottom-4 duration-700 delay-100">
            <span className="bg-primary/90 text-white px-4 py-1.5 rounded-full text-sm font-medium tracking-wide uppercase shadow-lg backdrop-blur-sm">
              #1 Marketplace for Algerian Crafts
            </span>
          </div>
          
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-white drop-shadow-2xl leading-tight font-heading animate-in slide-in-from-bottom-8 duration-700 delay-200">
            {t('heroTitle')}
          </h1>
          
          <p className="text-xl md:text-2xl text-stone-200 font-light max-w-2xl mx-auto leading-relaxed animate-in slide-in-from-bottom-8 duration-700 delay-300">
            {t('heroSubtitle')}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-in slide-in-from-bottom-8 duration-700 delay-400">
            <Link to="/marketplace" className="w-full sm:w-auto">
              <Button className="bg-primary text-white hover:bg-orange-700 text-lg px-10 py-6 h-auto w-full sm:min-w-[200px] rounded-none shadow-lg hover:-translate-y-1 transition-all">
                {t('shopNow')} <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/auth?role=seller" className="w-full sm:w-auto">
              <Button variant="outline" className="bg-white/10 text-white border-white/30 hover:bg-white hover:text-stone-900 rounded-none text-lg px-10 py-6 h-auto w-full sm:min-w-[200px] backdrop-blur-md hover:-translate-y-1 transition-all">
                {t('joinAsSeller')}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Trending Products Carousel */}
      <section className="py-20 max-w-7xl mx-auto px-4">
        <div className="flex justify-between items-end mb-10">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-2 flex items-center gap-3">
              <TrendingUp className="text-primary" /> Trending Now
            </h2>
            <p className="text-stone-500 text-lg">Most viewed items this week</p>
          </div>
          <Link to="/marketplace?sort=popular" className="hidden md:flex text-primary font-medium hover:underline items-center">
            View All <ArrowRight className="ml-1 w-4 h-4" />
          </Link>
        </div>

        <Carousel opts={{ align: "start", loop: true }} className="w-full">
          <CarouselContent className="-ml-4">
            {popularProducts.map((product) => (
              <CarouselItem key={product.id} className="pl-4 md:basis-1/2 lg:basis-1/3 xl:basis-1/4">
                <Link to={`/product/${product.id}`} className="group block h-full">
                  <div className="relative bg-white rounded-2xl overflow-hidden shadow-sm border border-stone-100 h-full hover:shadow-xl transition-all duration-300 group-hover:-translate-y-1">
                    <div className="aspect-[4/5] overflow-hidden bg-stone-100 relative">
                      <img 
                        src={product.image_url} 
                        alt={product.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                      <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full flex items-center text-xs font-bold text-stone-800 shadow-sm">
                        <Star className="w-3 h-3 text-yellow-500 mr-1 fill-yellow-500" /> {product.views}
                      </div>
                    </div>
                    <div className="p-5">
                      <h3 className="font-bold text-lg text-stone-900 truncate mb-1">{product.title}</h3>
                      <p className="text-stone-500 text-sm mb-3">{product.seller_name}</p>
                      <div className="flex justify-between items-center">
                        <span className="text-xl font-bold text-primary">{product.price} DZD</span>
                        <div className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-colors">
                          <ShoppingBag className="w-4 h-4" />
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="hidden md:flex -left-4" />
          <CarouselNext className="hidden md:flex -right-4" />
        </Carousel>
      </section>

      {/* Featured Categories (Bento Grid) */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-4">{t('featuredProducts')}</h2>
            <div className="w-20 h-1 bg-primary mx-auto rounded-full"></div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 grid-rows-2 gap-4 h-[600px]">
            <div className="md:col-span-2 md:row-span-2 relative group rounded-3xl overflow-hidden cursor-pointer" onClick={() => window.location.href='/marketplace?category=Rugs'}>
               <img src="https://images.unsplash.com/photo-1627726997943-6e397135f78a?crop=entropy&cs=srgb&fm=jpg&q=85" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" alt="Berber Rugs" />
               <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent flex flex-col justify-end p-8">
                 <h3 className="text-3xl text-white font-bold">Berber Rugs</h3>
                 <p className="text-stone-200 mt-2">Timeless geometric stories.</p>
               </div>
            </div>
            <div className="relative group rounded-3xl overflow-hidden cursor-pointer" onClick={() => window.location.href='/marketplace?category=Pottery'}>
               <img src="https://images.unsplash.com/photo-1516961642265-531546e84af2?q=80&w=1000&auto=format&fit=crop" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" alt="Pottery" />
               <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex flex-col justify-end p-6">
                 <h3 className="text-xl text-white font-bold">Clay Pottery</h3>
               </div>
            </div>
            <div className="relative group rounded-3xl overflow-hidden cursor-pointer" onClick={() => window.location.href='/marketplace?category=Jewelry'}>
               <img src="https://images.unsplash.com/photo-1535632066927-ab7c9ab60908?q=80&w=1000&auto=format&fit=crop" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" alt="Jewelry" />
               <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex flex-col justify-end p-6">
                 <h3 className="text-xl text-white font-bold">Silver Jewelry</h3>
               </div>
            </div>
             <div className="md:col-span-2 relative group rounded-3xl overflow-hidden cursor-pointer" onClick={() => window.location.href='/marketplace?category=Clothing'}>
               <img src="https://images.unsplash.com/photo-1585487000160-6ebcfceb0d03?q=80&w=1000&auto=format&fit=crop" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" alt="Clothing" />
               <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex flex-col justify-end p-8">
                 <h3 className="text-2xl text-white font-bold">Traditional Wear</h3>
                 <p className="text-stone-200">Elegance passed down through generations.</p>
               </div>
            </div>
          </div>
        </div>
      </section>

      {/* Meet Artisans */}
      <section className="py-20 bg-stone-100">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-between items-center mb-12">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-2 flex items-center gap-3">
                <Users className="text-primary" /> Meet Our Artisans
              </h2>
              <p className="text-stone-500">The hands behind the masterpieces</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {sellers.map((seller, index) => (
              <Link key={seller.id} to={`/store/${seller.id}`} className="bg-white rounded-2xl p-6 text-center shadow-sm hover:shadow-xl hover:-translate-y-2 transition-all duration-300 border border-stone-200">
                <div className={`w-24 h-24 rounded-full mx-auto mb-4 flex items-center justify-center text-3xl font-bold text-white shadow-lg ${['bg-orange-500', 'bg-teal-600', 'bg-blue-500', 'bg-red-500'][index % 4]}`}>
                  {seller.store_name?.charAt(0) || seller.name.charAt(0)}
                </div>
                <h3 className="text-xl font-bold text-stone-900 mb-1">{seller.store_name || seller.name}</h3>
                <p className="text-stone-500 text-sm mb-4">Joined 2024</p>
                <Button variant="outline" className="w-full rounded-full border-stone-200 hover:border-primary hover:text-primary">
                  Visit Store
                </Button>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-primary relative overflow-hidden">
        <div className="relative z-10 max-w-4xl mx-auto text-center px-4">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Ready to share your craft with the world?</h2>
          <p className="text-xl text-orange-100 mb-10">Join hundreds of Algerian artisans growing their business on MadeinDZ.</p>
          <Link to="/auth?role=seller">
             <Button size="lg" className="bg-white text-primary hover:bg-stone-100 rounded-full px-10 py-7 text-xl font-bold shadow-2xl">
              Start Selling Today
            </Button>
          </Link>
        </div>
      </section>

      {/* Simple Footer */}
      <footer className="bg-stone-900 text-stone-400 py-8 text-center text-sm">
        <p>© {new Date().getFullYear()} MadeinDZ. All rights reserved.</p>
      </footer>
    </div>
  );
}
